import React from "react";
import ExpandedMonitorChart from "./ExpandedMonitorChart";
import { detectPlatform, getPlatformIcon } from "../utils/platformDetection";
import { calculateIncidents, formatDuration, formatTimestamp } from "../utils/incidents";
import { fetchJSON, apiFetch } from "../apiFetch";
import { createLogger } from "../utils/logger";
import { useToast } from "../toast";

const logger = createLogger('SiteDetailsModal');

// Common HTTP status code suggestions
const STATUS_CODE_SUGGESTIONS = [
  "2xx",
  "3xx",
  "4xx",
  "5xx",
  "200",
  "201",
  "202",
  "204",
  "206",
  "301",
  "302",
  "303",
  "304",
  "307",
  "308",
  "400",
  "401",
  "403",
  "404",
  "405",
  "408",
  "409",
  "410",
  "429",
  "500",
  "502",
  "503",
  "504",
];

export default function SiteDetailsModal({ monitorId, onClose }) {
  const [monitor, setMonitor] = React.useState(null);
  const [history, setHistory] = React.useState([]);
  const [summary, setSummary] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [incidents, setIncidents] = React.useState([]);
  const [isEditing, setIsEditing] = React.useState(false);
  const [editingStatusCodes, setEditingStatusCodes] = React.useState([]);
  const [statusCodeInput, setStatusCodeInput] = React.useState("");
  const [showSuggestions, setShowSuggestions] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const modalRef = React.useRef(null);
  const closeButtonRef = React.useRef(null);
  const previousActiveElementRef = React.useRef(null);
  const { showToast } = useToast();

  // Handle escape key and focus management
  React.useEffect(() => {
    if (!monitorId) return;
    
    // Store the previously focused element
    previousActiveElementRef.current = document.activeElement;
    
    // Focus the modal when it opens
    if (modalRef.current) {
      const focusableElements = modalRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstFocusable = focusableElements[0];
      if (firstFocusable) {
        firstFocusable.focus();
      }
    }
    
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    
    const handleTab = (e) => {
      // Only handle Tab key presses
      if (e.key !== 'Tab') return;
      
      if (!modalRef.current) return;
      
      const focusableElements = Array.from(modalRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )).filter(el => !el.disabled && el.offsetParent !== null);
      
      if (focusableElements.length === 0) return;
      
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      
      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      } else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    };
    
    document.addEventListener("keydown", handleEscape);
    document.addEventListener("keydown", handleTab);
    
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.removeEventListener("keydown", handleTab);
      // Restore focus to previously focused element
      if (previousActiveElementRef.current) {
        previousActiveElementRef.current.focus();
      }
    };
  }, [monitorId, onClose]);

  // Prevent body scroll when modal is open
  React.useEffect(() => {
    if (!monitorId) return;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [monitorId]);

  // Load monitor data
  React.useEffect(() => {
    if (!monitorId) return;

    async function loadData() {
      setLoading(true);
      try {
        // Load monitor details
        const { monitors } = await fetchJSON("/api/monitors");
        const foundMonitor = monitors.find((m) => m.id === monitorId);
        setMonitor(foundMonitor || null);

        if (foundMonitor) {
          // Initialize editing status codes from monitor
          const currentCodes = foundMonitor.acceptableStatusCodes || [];
          setEditingStatusCodes(
            Array.isArray(currentCodes)
              ? currentCodes.map((c) => String(c))
              : []
          );

          // Load history (7 days worth - 168 hours * 12 checks per hour = ~2016 checks)
          const historyData = await fetchJSON(
            `/api/history?id=${encodeURIComponent(monitorId)}&limit=2016`
          );
          setHistory(historyData.rows || []);

          // Load summary
          const summaryData = await fetchJSON("/api/summary?window=7d");
          setSummary(summaryData.summaries?.[monitorId] || null);

          // Calculate incidents
          const allIncidents = calculateIncidents([foundMonitor], {
            [monitorId]: historyData.rows || [],
          });
          setIncidents(allIncidents);
        }
      } catch (error) {
        logger.error("Failed to load site details:", error);
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [monitorId]);

  // Filter suggestions based on input
  const filteredSuggestions = React.useMemo(() => {
    if (!statusCodeInput.trim()) {
      return STATUS_CODE_SUGGESTIONS;
    }
    const input = statusCodeInput.toLowerCase().trim();
    return STATUS_CODE_SUGGESTIONS.filter(
      (suggestion) =>
        suggestion.toLowerCase().includes(input) &&
        !editingStatusCodes.includes(suggestion)
    );
  }, [statusCodeInput, editingStatusCodes]);

  // Add status code tag
  const addStatusCode = (code) => {
    const trimmed = code.trim();
    if (
      trimmed &&
      !editingStatusCodes.includes(trimmed) &&
      (trimmed.match(/^\d+$/) || // Single number
        trimmed.match(/^\d+-\d+$/) || // Range like 200-299
        trimmed.match(/^\d+xx$/) || // Wildcard like 2xx
        trimmed.match(/^\d{3}$/)) // 3-digit code
    ) {
      setEditingStatusCodes([...editingStatusCodes, trimmed]);
      setStatusCodeInput("");
      setShowSuggestions(false);
    }
  };

  // Remove status code tag
  const removeStatusCode = (code) => {
    setEditingStatusCodes(editingStatusCodes.filter((c) => c !== code));
  };

  // Handle save
  const handleSave = async () => {
    if (!monitor) return;

    setSaving(true);
    try {
      // Parse acceptable status codes from tags array
      let parsedStatusCodes = undefined;
      if (editingStatusCodes && editingStatusCodes.length > 0) {
        parsedStatusCodes = editingStatusCodes.map((code) => {
          // Check if it's a range format (e.g., "200-299" or "2xx")
          if (code.includes("-") || code.endsWith("xx")) {
            return code; // Keep as string for ranges and wildcards
          }
          // Try to parse as number
          const num = parseInt(code, 10);
          return isNaN(num) ? code : num;
        });
      }

      const body = {};
      if (parsedStatusCodes && parsedStatusCodes.length > 0) {
        body.acceptableStatusCodes = parsedStatusCodes;
      } else {
        // If empty array, send empty array to clear it
        body.acceptableStatusCodes = [];
      }

      await apiFetch(`/api/monitors?id=${encodeURIComponent(monitor.id)}`, {
        method: "PUT",
        body: JSON.stringify(body),
      });

      // Update local monitor state
      const updatedMonitor = {
        ...monitor,
        acceptableStatusCodes: parsedStatusCodes && parsedStatusCodes.length > 0 ? parsedStatusCodes : undefined,
      };
      setMonitor(updatedMonitor);
      setIsEditing(false);
      setStatusCodeInput("");
      setShowSuggestions(false);

      showToast({
        type: "success",
        title: "Monitor updated",
        message: "Acceptable status codes have been updated.",
      });

      // Reload data to get fresh state
      const { monitors } = await fetchJSON("/api/monitors");
      const foundMonitor = monitors.find((m) => m.id === monitorId);
      if (foundMonitor) {
        setMonitor(foundMonitor);
        const currentCodes = foundMonitor.acceptableStatusCodes || [];
        setEditingStatusCodes(
          Array.isArray(currentCodes)
            ? currentCodes.map((c) => String(c))
            : []
        );
      }
    } catch (err) {
      logger.error("Failed to update monitor:", err);
      showToast({
        type: "error",
        title: "Update failed",
        message: err?.message || String(err),
      });
    } finally {
      setSaving(false);
    }
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    if (!monitor) return;
    const currentCodes = monitor.acceptableStatusCodes || [];
    setEditingStatusCodes(
      Array.isArray(currentCodes)
        ? currentCodes.map((c) => String(c))
        : []
    );
    setStatusCodeInput("");
    setShowSuggestions(false);
    setIsEditing(false);
  };

  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!monitorId) return null;

  const platform = monitor ? detectPlatform(monitor.url) : null;
  const platformIcon = platform ? getPlatformIcon(platform) : null;
  const isUp = history.length > 0 && history[0]?.ok;
  const latestCheck = history.length > 0 ? history[0] : null;

  const formatRelativeTime = (ts) => {
    const now = Date.now();
    const diff = now - ts;
    const minutes = Math.floor(diff / (60 * 1000));
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days} day${days !== 1 ? "s" : ""} ago`;
    if (hours > 0) return `${hours} hour${hours !== 1 ? "s" : ""} ago`;
    if (minutes > 0) return `${minutes} minute${minutes !== 1 ? "s" : ""} ago`;
    return "Just now";
  };

  const formatFullTimestamp = (ts) => {
    return new Date(ts).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      timeZoneName: "short",
    });
  };

  const formatShortTimestamp = (ts) => {
    return new Date(ts).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZoneName: "short",
    });
  };

  return (
    <div 
      className="site-details-overlay" 
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="site-details-title"
    >
      <div 
        className="site-details-modal" 
        onClick={(e) => e.stopPropagation()}
        ref={modalRef}
        role="document"
      >
        {loading ? (
          <div className="site-details-loading">Loading site details...</div>
        ) : !monitor ? (
          <div className="site-details-error">Monitor not found</div>
        ) : (
          <>
            <div className="site-details-header">
              <div>
                <h2 className="site-details-title" id="site-details-title">{monitor.name}</h2>
                <div className="site-details-subtitle">
                  <a
                    href={monitor.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="site-details-link"
                  >
                    {monitor.url}
                  </a>
                  {platform && (
                    <span
                      className="platform-badge"
                      style={{
                        backgroundColor: `${platform.color}15`,
                        color: platform.color,
                        borderColor: `${platform.color}40`,
                      }}
                    >
                      {platformIcon && (
                        <span className="material-icons platform-icon">
                          {platformIcon}
                        </span>
                      )}
                      {platform.name}
                    </span>
                  )}
                </div>
              </div>
              <button
                className="site-details-close"
                onClick={onClose}
                aria-label="Close monitor details"
                ref={closeButtonRef}
                type="button"
              >
                <span className="material-icons" aria-hidden="true">close</span>
              </button>
            </div>

            <div className="site-details-content">
              {/* Status Overview */}
              <div className="site-details-section">
                <h3 className="site-details-section-title">Status Overview</h3>
                <div className="site-details-grid">
                  <div className="site-details-item">
                    <div className="site-details-label">Current Status</div>
                    <div className="site-details-value">
                      <span
                        className={`status-badge ${
                          isUp ? "status-up" : "status-down"
                        }`}
                        style={{ display: "inline-flex", alignItems: "center", gap: "8px", padding: "6px 12px" }}
                      >
                        <span
                          className={`monitoring-status-indicator ${
                            isUp ? "status-indicator-up" : "status-indicator-down"
                          }`}
                        >
                          <span className="status-indicator-dot" aria-hidden="true"></span>
                        </span>
                        {isUp ? "Up" : "Down"}
                      </span>
                    </div>
                  </div>
                  {summary && (
                    <>
                      <div className="site-details-item">
                        <div className="site-details-label">7-Day Uptime</div>
                        <div className="site-details-value">
                          {summary.uptimePct
                            ? `${summary.uptimePct.toFixed(2)}%`
                            : "—"}
                        </div>
                      </div>
                      <div className="site-details-item">
                        <div className="site-details-label">Avg Response Time</div>
                        <div className="site-details-value">
                          {summary.avgLatencyMs
                            ? `${(summary.avgLatencyMs / 1000).toFixed(2)}s`
                            : "—"}
                        </div>
                      </div>
                    </>
                  )}
                  {latestCheck && (
                    <div className="site-details-item">
                      <div className="site-details-label">Last Checked</div>
                      <div className="site-details-value" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
                        {formatShortTimestamp(latestCheck.ts)}
                        <div className="site-details-meta">
                          {formatRelativeTime(latestCheck.ts)}
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="site-details-item">
                    <div className="site-details-label">Check Interval</div>
                    <div className="site-details-value">
                      {monitor.intervalMinutes || 5} minutes
                    </div>
                  </div>
                </div>
              </div>

              {/* Uptime Chart */}
              {history.length > 0 && (
                <div className="site-details-section">
                  <h3 className="site-details-section-title">Uptime Chart (7 Days)</h3>
                  <div className="site-details-chart">
                    <ExpandedMonitorChart data={history} />
                  </div>
                </div>
              )}

              {/* Activity Log */}
              <div className="site-details-section">
                <h3 className="site-details-section-title">Activity Log</h3>
                {incidents.length > 0 ? (
                  <div className="site-details-activity-log">
                    <div className="activity-log-header">
                      <div className="activity-col-time">Time</div>
                      <div className="activity-col-status">Status</div>
                      <div className="activity-col-cause">Root Cause</div>
                      <div className="activity-col-duration">Duration</div>
                    </div>
                    <div className="activity-log-body">
                      {incidents.slice(0, 50).map((incident, idx) => (
                        <div key={idx} className="activity-log-row">
                          <div className="activity-col-time">
                            {formatTimestamp(incident.startTime)}
                            {incident.endTime && (
                              <div className="activity-log-meta">
                                Resolved: {formatTimestamp(incident.endTime)}
                              </div>
                            )}
                          </div>
                          <div className="activity-col-status">
                            {incident.recovered ? (
                              <span className="status-badge status-resolved">
                                <span className="material-icons">check_circle</span>
                                Resolved
                              </span>
                            ) : (
                              <span className="status-badge status-ongoing">
                                <span className="status-dot"></span>
                                Ongoing
                              </span>
                            )}
                          </div>
                          <div className="activity-col-cause">
                            <span
                              className={`root-cause-badge ${
                                incident.rootCauseType === "HTTP"
                                  ? "root-cause-http"
                                  : "root-cause-timeout"
                              }`}
                            >
                              {incident.rootCause}
                            </span>
                            {incident.rootCauseType === "TIMEOUT" && (
                              <span className="root-cause-text">
                                Connection Timeout
                              </span>
                            )}
                          </div>
                          <div className="activity-col-duration">
                            {formatDuration(incident.startTime, incident.endTime)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="site-details-empty">No incidents recorded</div>
                )}
              </div>

              {/* Monitor Information */}
              <div className="site-details-section">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                  <h3 className="site-details-section-title">Monitor Information</h3>
                  {!isEditing && (
                    <button
                      type="button"
                      onClick={() => setIsEditing(true)}
                      style={{
                        padding: "6px 12px",
                        fontSize: "13px",
                        fontWeight: "500",
                        fontFamily: '"Poppins", ui-sans-serif, system-ui, -apple-system, sans-serif',
                        border: "1px solid var(--border)",
                        borderRadius: "6px",
                        background: "var(--panel)",
                        color: "var(--text)",
                        cursor: "pointer",
                        transition: "all 0.2s",
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = "var(--panel-hover)";
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = "var(--panel)";
                      }}
                    >
                      Edit Settings
                    </button>
                  )}
                </div>
                <div className="site-details-grid">
                  <div className="site-details-item">
                    <div className="site-details-label">Monitor ID</div>
                    <div className="site-details-value site-details-value-mono">
                      {monitor.id}
                    </div>
                  </div>
                  <div className="site-details-item">
                    <div className="site-details-label">HTTP Method</div>
                    <div className="site-details-value">
                      {monitor.httpMethod || "HEAD"}
                    </div>
                  </div>
                  <div className="site-details-item" style={{ gridColumn: "1 / -1" }}>
                    <div className="site-details-label">Acceptable Status Codes</div>
                    {isEditing ? (
                      <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                        <div
                          style={{
                            position: "relative",
                            border: "1px solid var(--border)",
                            borderRadius: "6px",
                            background: "var(--panel)",
                            padding: "8px 12px",
                            minHeight: "42px",
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "6px",
                            alignItems: "center",
                            cursor: "text",
                          }}
                          onClick={() => {
                            document.getElementById("status-code-edit-input")?.focus();
                          }}
                        >
                          {/* Display tags */}
                          {editingStatusCodes.map((code) => (
                            <div
                              key={code}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "6px",
                                padding: "4px 8px",
                                background: "var(--accent-fade)",
                                border: "1px solid var(--accent)",
                                borderRadius: "4px",
                                fontSize: "13px",
                                fontFamily: '"Poppins", ui-sans-serif, system-ui, -apple-system, sans-serif',
                                color: "var(--text)",
                                fontWeight: "500",
                              }}
                            >
                              <span>{code}</span>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeStatusCode(code);
                                }}
                                style={{
                                  background: "none",
                                  border: "none",
                                  color: "var(--text)",
                                  cursor: "pointer",
                                  padding: "0",
                                  display: "flex",
                                  alignItems: "center",
                                  fontSize: "16px",
                                  lineHeight: "1",
                                  opacity: 0.7,
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.opacity = "1";
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.opacity = "0.7";
                                }}
                              >
                                ×
                              </button>
                            </div>
                          ))}
                          {/* Input field */}
                          <input
                            id="status-code-edit-input"
                            type="text"
                            value={statusCodeInput}
                            onChange={(e) => {
                              setStatusCodeInput(e.target.value);
                              setShowSuggestions(true);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && statusCodeInput.trim()) {
                                e.preventDefault();
                                if (filteredSuggestions.length > 0) {
                                  addStatusCode(filteredSuggestions[0]);
                                } else {
                                  addStatusCode(statusCodeInput);
                                }
                              } else if (
                                e.key === "Backspace" &&
                                !statusCodeInput &&
                                editingStatusCodes.length > 0
                              ) {
                                removeStatusCode(editingStatusCodes[editingStatusCodes.length - 1]);
                              } else if (e.key === "Escape") {
                                setShowSuggestions(false);
                              }
                            }}
                            onFocus={() => setShowSuggestions(true)}
                            onBlur={() => {
                              setTimeout(() => setShowSuggestions(false), 200);
                            }}
                            placeholder={
                              editingStatusCodes.length === 0
                                ? "Click to add status code (e.g., 403)..."
                                : ""
                            }
                            style={{
                              flex: "1",
                              minWidth: "120px",
                              border: "none",
                              background: "transparent",
                              color: "var(--text)",
                              fontSize: "13px",
                              fontFamily: '"Poppins", ui-sans-serif, system-ui, -apple-system, sans-serif',
                              outline: "none",
                              padding: "0",
                            }}
                          />
                          {/* Suggestions dropdown */}
                          {showSuggestions && filteredSuggestions.length > 0 && (
                            <div
                              style={{
                                position: "absolute",
                                top: "100%",
                                left: 0,
                                right: 0,
                                marginTop: "4px",
                                background: "var(--panel)",
                                border: "1px solid var(--border)",
                                borderRadius: "6px",
                                maxHeight: "200px",
                                overflowY: "auto",
                                zIndex: 1000,
                                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
                              }}
                            >
                              {filteredSuggestions.map((suggestion) => (
                                <div
                                  key={suggestion}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    addStatusCode(suggestion);
                                  }}
                                  onMouseDown={(e) => e.preventDefault()}
                                  style={{
                                    padding: "10px 12px",
                                    fontSize: "13px",
                                    fontFamily: '"Poppins", ui-sans-serif, system-ui, -apple-system, sans-serif',
                                    color: "var(--text)",
                                    cursor: "pointer",
                                    borderBottom: "1px solid var(--border)",
                                    transition: "background 0.15s",
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.background = "var(--panel-hover)";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.background = "transparent";
                                  }}
                                >
                                  {suggestion}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        <div
                          style={{
                            fontSize: "12px",
                            color: "var(--text-muted)",
                            lineHeight: "1.5",
                            fontFamily: '"Poppins", ui-sans-serif, system-ui, -apple-system, sans-serif',
                          }}
                        >
                          {editingStatusCodes.length === 0
                            ? "Default: 200-299 (standard success). Add status codes to accept (e.g., 403 for sites that return Forbidden but are still up)."
                            : "We will create an incident when we receive an HTTP status code other than those defined above."}
                        </div>
                        <div style={{ display: "flex", gap: "8px" }}>
                          <button
                            type="button"
                            onClick={handleSave}
                            disabled={saving}
                            style={{
                              padding: "8px 16px",
                              fontSize: "13px",
                              fontWeight: "500",
                              fontFamily: '"Poppins", ui-sans-serif, system-ui, -apple-system, sans-serif',
                              border: "none",
                              borderRadius: "6px",
                              background: "var(--accent)",
                              color: "white",
                              cursor: saving ? "not-allowed" : "pointer",
                              opacity: saving ? 0.6 : 1,
                            }}
                          >
                            {saving ? "Saving..." : "Save"}
                          </button>
                          <button
                            type="button"
                            onClick={handleCancelEdit}
                            disabled={saving}
                            style={{
                              padding: "8px 16px",
                              fontSize: "13px",
                              fontWeight: "500",
                              fontFamily: '"Poppins", ui-sans-serif, system-ui, -apple-system, sans-serif',
                              border: "1px solid var(--border)",
                              borderRadius: "6px",
                              background: "var(--panel)",
                              color: "var(--text)",
                              cursor: saving ? "not-allowed" : "pointer",
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="site-details-value">
                        {monitor.acceptableStatusCodes && monitor.acceptableStatusCodes.length > 0
                          ? Array.isArray(monitor.acceptableStatusCodes)
                            ? monitor.acceptableStatusCodes.join(", ")
                            : String(monitor.acceptableStatusCodes)
                          : "200-299 (default)"}
                      </div>
                    )}
                  </div>
                  {monitor.createdAt && (
                    <div className="site-details-item">
                      <div className="site-details-label">Created</div>
                      <div className="site-details-value">
                        {formatFullTimestamp(monitor.createdAt)}
                        <div className="site-details-meta">
                          {formatRelativeTime(monitor.createdAt)}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="site-details-footer">
              <button 
                className="btn btn-primary" 
                onClick={onClose}
                type="button"
                aria-label="Close monitor details"
              >
                Close
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

